import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import type { KnowledgeBaseDocument } from '@/types/supabase';
import { generateEmbedding } from '@/lib/embeddings';

export function useKnowledgeBaseDocuments() {
  const { toast } = useToast();

  const fetchKnowledgeBaseDocuments = useCallback(async (knowledgeBaseId: string) => {
    try {
      const { data, error } = await supabase
        .from('knowledge_base_documents')
        .select('*')
        .eq('knowledge_base_id', knowledgeBaseId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data ?? []) as KnowledgeBaseDocument[];
    } catch (error) {
      console.error('Error fetching knowledge base documents:', error);
      toast({ title: 'Error', description: 'Failed to fetch documents.', variant: 'destructive' });
      return [];
    }
  }, [toast]);

  const addDocument = useCallback(async (knowledgeBaseId: string, document: {
    name: string;
    content: string;
    fileType: string;
    size: number;
    storagePath?: string | null;
  }) => {
    try {
      const embedding = await generateEmbedding(document.content);
      const payload: Record<string, unknown> = {
        knowledge_base_id: knowledgeBaseId,
        name: document.name,
        content: document.content,
        file_type: document.fileType,
        size: document.size,
        storage_path: document.storagePath ?? null,
      };

      if (embedding) {
        payload.embedding = embedding;
      }

      const { data, error } = await (supabase as any)
        .from('knowledge_base_documents')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;
      return data as KnowledgeBaseDocument;
    } catch (error) {
      console.error('Error adding document:', error);
      toast({ title: 'Error', description: 'Failed to add document.', variant: 'destructive' });
      return null;
    }
  }, [toast]);

  const updateDocument = useCallback(async (documentId: string, document: {
    name: string;
    content: string;
    fileType?: string;
    size?: number;
  }) => {
    try {
      const embedding = await generateEmbedding(document.content);
      const updates: Record<string, unknown> = {
        name: document.name,
        content: document.content,
        file_type: document.fileType,
        size: document.size,
        updated_at: new Date().toISOString(),
      };

      if (embedding) {
        updates.embedding = embedding;
      }

      const { data, error } = await (supabase as any)
        .from('knowledge_base_documents')
        .update(updates)
        .eq('id', documentId)
        .select('*')
        .single();

      if (error) throw error;
      return data as KnowledgeBaseDocument;
    } catch (error) {
      console.error('Error updating document:', error);
      toast({ title: 'Error', description: 'Failed to update document.', variant: 'destructive' });
      return null;
    }
  }, [toast]);

  const deleteDocument = useCallback(async (documentId: string) => {
    try {
      const { error } = await supabase
        .from('knowledge_base_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({ title: 'Error', description: 'Failed to delete document.', variant: 'destructive' });
      return false;
    }
  }, [toast]);

  return {
    fetchKnowledgeBaseDocuments,
    addDocument,
    updateDocument,
    deleteDocument,
  };
}